import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAppContext } from '../context/AppContext';
import { apiFetch } from '../apiFetch';
import { applyTheme } from '../theme/theme-init';

function formatProvisionCountdown(ms) {
  if (ms <= 0) return 'expirado';
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}


function Settings({ usuario }) {
  const [playerProvision, setPlayerProvision] = useState(null);
  const [playerProvisionLoading, setPlayerProvisionLoading] = useState(false);
  const [playerProvisionError, setPlayerProvisionError] = useState('');
  const [colaboradorProvision, setColaboradorProvision] = useState(null);
  const [colaboradorProvisionLoading, setColaboradorProvisionLoading] = useState(false);
  const [colaboradorProvisionError, setColaboradorProvisionError] = useState('');
  const [provisionNow, setProvisionNow] = useState(Date.now());
  const [tema, setTema] = useState(() => {
    const saved = localStorage.getItem('radio-ai-theme');
    return saved === 'dark' || saved === 'light' ? saved : 'auto';
  });
  useEffect(() => {
    if (!playerProvision?.expiresAt && !colaboradorProvision?.expiresAt) return undefined;
    const timer = setInterval(() => setProvisionNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [playerProvision?.expiresAt, colaboradorProvision?.expiresAt]);

  const gerarProvisionamentoPlayer = async () => {
    setPlayerProvisionLoading(true);
    setPlayerProvisionError('');

    try {
      const res = await apiFetch('/api/auth/player-provision', { method: 'POST' });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || 'Erro ao gerar código');
      }

      setPlayerProvision(data);
      setProvisionNow(Date.now());
    } catch (err) {
      setPlayerProvisionError(err.message || 'Erro ao gerar código');
    } finally {
      setPlayerProvisionLoading(false);
    }
  };

  const gerarProvisionamentoColaborador = async () => {
    setColaboradorProvisionLoading(true);
    setColaboradorProvisionError('');

    try {
      const res = await apiFetch('/api/auth/colaborador-provision', { method: 'POST' });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || 'Erro ao gerar pareamento');
      }

      setColaboradorProvision(data);
      setProvisionNow(Date.now());
    } catch (err) {
      setColaboradorProvisionError(err.message || 'Erro ao gerar pareamento');
    } finally {
      setColaboradorProvisionLoading(false);
    }
  };

  const provisionMsRemaining = playerProvision?.expiresAt
    ? Date.parse(playerProvision.expiresAt) - provisionNow
    : 0;
  const provisionExpired = Boolean(playerProvision?.expiresAt && provisionMsRemaining <= 0);
  const colaboradorProvisionMsRemaining = colaboradorProvision?.expiresAt
    ? Date.parse(colaboradorProvision.expiresAt) - provisionNow
    : 0;
  const colaboradorProvisionExpired = Boolean(colaboradorProvision?.expiresAt && colaboradorProvisionMsRemaining <= 0);

  return (
    <div className="page">
      <h1 className="page-title">Sistema</h1>
      <div className="page-content">
        <div className="card" style={{ maxWidth: 720, width: '100%' }}>
                {/* ── Tema da interface ─────────────────────────── */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 0', borderBottom: '1px solid rgba(var(--neutral-300-rgb),0.3)'
                }}>
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>
                      Tema da interface
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {tema === 'dark' ? 'Escuro' : tema === 'light' ? 'Claro' : 'Automático (segue o sistema)'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['dark', 'auto', 'light'].map(t => (
                      <button
                        key={t}
                        onClick={() => {
                          const novo = t === 'auto' ? null : t
                          if (novo) {
                            applyTheme(novo)
                          } else {
                            localStorage.removeItem('radio-ai-theme')
                            const osTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
                            document.documentElement.setAttribute('data-theme', osTheme)
                          }
                          setTema(t)
                        }}
                        style={{
                          padding: '4px 10px', borderRadius: 'var(--radius-md)', fontSize: 12,
                          cursor: 'pointer', border: '1px solid',
                          borderColor: tema === t ? 'var(--action)' : 'var(--border)',
                          background: tema === t ? 'var(--action-subtle)' : 'transparent',
                          color: tema === t ? 'var(--text-primary)' : 'var(--text-muted)',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {t === 'dark' ? 'Escuro' : t === 'light' ? 'Claro' : 'Auto'}
                      </button>
                    ))}
                  </div>
                </div>
        </div>
        <div style={{ maxWidth: 720, width: '100%' }}>
          {usuario.tipoProduto === 'indoor' && usuario.id && (
            <div style={{ marginBottom: 8 }}>
              <div style={{
                padding: '14px 16px', borderRadius: 0, marginBottom: 16,
                background: 'rgba(var(--brand-rgb),0.08)', border: '1px solid rgba(var(--brand-rgb),0.25)',
              }}>
                <p style={{ color: 'var(--brand-400)', fontSize: 13, margin: '0 0 4px', fontWeight: 600 }}>
                  Provisionar Radio Player
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 14px', lineHeight: 1.5 }}>
                  Abra o Radio Player primeiro, depois clique no link. Se o link não abrir, use o código manual no exe.
                </p>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={gerarProvisionamentoPlayer}
                    disabled={playerProvisionLoading}
                    style={{ fontSize: 12, padding: '7px 14px' }}
                  >
                    {playerProvisionLoading
                      ? 'Gerando...'
                      : playerProvision && provisionExpired
                        ? 'Gerar novo código'
                        : 'Provisionar Radio Player'}
                  </button>

                  {playerProvision?.deepLink && !provisionExpired && (
                    <a
                      className="btn"
                      href={playerProvision.deepLink}
                      style={{ fontSize: 12, padding: '7px 14px', textDecoration: 'none' }}
                    >
                      Abrir no Radio Player
                    </a>
                  )}
                </div>

                {playerProvision && (
                  <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      Expira em {formatProvisionCountdown(provisionMsRemaining)}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        readOnly
                        value={playerProvision.code || ''}
                        style={{
                          width: 130,
                          background: 'rgba(var(--neutral-500-rgb),0.6)',
                          border: '1px solid rgba(var(--neutral-300-rgb),0.5)',
                          borderRadius: 6, padding: '7px 10px',
                          color: 'var(--text-primary)', fontSize: 13,
                          fontFamily: 'monospace', letterSpacing: '0.08em',
                        }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Código manual de 6 caracteres
                      </span>
                    </div>
                  </div>
                )}

                {playerProvisionError && (
                  <div className="alert alert-error" style={{ marginTop: 12 }}>
                    {playerProvisionError}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* APP DO COLABORADOR — indoor only, após salvar */}
          {/* ═══════════════════════════════════════════ */}
          {usuario.tipoProduto === 'indoor' && usuario.id && (
            <div style={{ marginBottom: 8 }}>
              <div style={{
                padding: '14px 16px', borderRadius: 0, marginBottom: 16,
                background: 'rgba(var(--brand-rgb),0.08)', border: '1px solid rgba(var(--brand-rgb),0.25)',
              }}>
                <p style={{ color: 'var(--brand-400)', fontSize: 13, margin: '0 0 4px', fontWeight: 600 }}>
                  📱 App do Colaborador
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 14px', lineHeight: 1.5 }}>
                  Gere um pareamento temporário para autorizar este celular. O QR expira e não contém segredo permanente.
                </p>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
                  <button
                    className="btn btn-secondary"
                    onClick={gerarProvisionamentoColaborador}
                    disabled={colaboradorProvisionLoading}
                    style={{ fontSize: 12, padding: '7px 14px' }}
                  >
                    {colaboradorProvisionLoading
                      ? 'Gerando...'
                      : colaboradorProvision && colaboradorProvisionExpired
                        ? 'Gerar novo pareamento'
                        : 'Gerar pareamento do app'}
                  </button>

                  {colaboradorProvision && (
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      Expira em {formatProvisionCountdown(colaboradorProvisionMsRemaining)}
                    </span>
                  )}
                </div>

                {colaboradorProvision?.qrPayload && !colaboradorProvisionExpired && (
                  <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
                    <div style={{ background: '#fff', padding: 10, borderRadius: 8, flexShrink: 0 }}>
                      <QRCodeSVG
                        value={JSON.stringify(colaboradorProvision.qrPayload)}
                        size={180}
                      />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                        Pareamento seguro
                      </div>
                      <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 8px', lineHeight: 1.5 }}>
                        Use este QR no app atualizado. Depois de usado ou expirado, ele não pareia outro aparelho.
                      </p>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        Escopos: {(colaboradorProvision.scopes || []).join(', ') || 'colaborador:anuncio'}
                      </div>
                    </div>
                  </div>
                )}

                {colaboradorProvisionError && (
                  <div className="alert alert-error" style={{ marginTop: 12 }}>
                    {colaboradorProvisionError}
                  </div>
                )}

                <div style={{ borderTop: '1px solid rgba(var(--neutral-300-rgb),0.25)', marginTop: 12, paddingTop: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 6, fontWeight: 600 }}>
                    Compatibilidade temporária com APK antigo
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      readOnly
                      value={usuario.id}
                      style={{
                        flex: 1, minWidth: 0,
                        background: 'rgba(var(--neutral-500-rgb),0.6)',
                        border: '1px solid rgba(var(--neutral-300-rgb),0.5)',
                        borderRadius: 6, padding: '7px 10px',
                        color: 'var(--text-primary)', fontSize: 12,
                        fontFamily: 'monospace', letterSpacing: '0.03em',
                      }}
                    />
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: 12, padding: '7px 14px', whiteSpace: 'nowrap' }}
                      onClick={() => {
                        navigator.clipboard.writeText(usuario.id);
                      }}
                    >
                      Copiar
                    </button>
                  </div>
                  <p style={{ color: 'var(--text-muted)', fontSize: 11, margin: '8px 0 0', lineHeight: 1.4 }}>
                    Este código será removido após a migração do app para sessão de dispositivo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* QR Code de download/atualização do APK do colaborador */}
          {usuario.tipoProduto === 'indoor' && usuario.id && (
            <div style={{ marginBottom: 8 }}>
              <div style={{
                padding: '14px 16px', borderRadius: 0, marginBottom: 16,
                background: 'rgba(var(--brand-rgb),0.08)', border: '1px solid rgba(var(--brand-rgb),0.25)',
              }}>
                <p style={{ color: 'var(--brand-400)', fontSize: 13, margin: '0 0 4px', fontWeight: 600 }}>
                  Atualizar App do Colaborador
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 16px' }}>
                  Colaboradores escaneiam para instalar ou atualizar o app
                </p>
                <div style={{ textAlign: 'center' }}>
                  <QRCodeSVG
                    value="https://iafm.io/download/colaborador.apk"
                    size={180}
                    bgColor="#0f172a"
                    fgColor="#ffffff"
                    level="M"
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 12 }}>
                    iafm.io/download/colaborador.apk
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>
      </div>
    </div>
  );
}

export default function SystemScreen() {
  const { usuario } = useAppContext();
  return <Settings key={usuario.id || 'unsaved'} usuario={usuario} />;
}
